<template>
  <!-- Dashboard sentence search (design B): a dark band with a segmented tab
       bar; each tab opens its own popup with a fill-in-the-blanks sentence -->
  <div class="ssb">
    <div class="container">
      <div class="ssb-inner">
        <div class="ssb-intro">
          <span class="ssb-eyebrow"><i class="fa fa-magic"></i> Smart search</span>
          <h4 class="ssb-heading">Build a list of&hellip;</h4>
        </div>

        <div class="ssb-seg">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            type="button"
            class="ssb-seg-btn"
            :style="{ '--tab-color': tab.color }"
            @click="openTab(tab)"
          >
            <i class="fa" :class="tab.icon"></i>
            <span>{{ tab.label }}</span>
          </button>
        </div>
      </div>
    </div>

    <teleport to="body">
      <transition name="ssp-fade">
        <div v-if="activeTab" class="ssp-overlay" @mousedown.self="closeModal">
          <div
            class="ssp"
            role="dialog"
            aria-modal="true"
            :aria-label="activeTab.title"
            :style="{ '--tab-color': activeTab.color }"
          >
            <!-- left: what the search is, and a live read-back of it -->
            <aside class="ssp-aside">
              <span class="ssp-icon"><i class="fa" :class="activeTab.icon"></i></span>
              <h5 class="ssp-title">{{ activeTab.title }}</h5>
              <p class="ssp-hint">{{ activeTab.hint }}</p>

              <div class="ssp-summary">
                <div class="ssp-summary-head">Your search</div>
                <div v-for="key in activeFields" :key="key" class="ssp-summary-row">
                  <span class="ssp-summary-label">
                    {{ fields[key].summaryLabel || fields[key].label }}
                  </span>
                  <span
                    class="ssp-summary-value"
                    :class="{ 'ssp-any': !summaryValue(key) }"
                  >
                    {{ summaryValue(key) || "Any" }}
                  </span>
                </div>
              </div>
            </aside>

            <div class="ssp-main">
              <div class="ssp-main-head">
                <span class="ssp-step">Complete the sentence</span>
                <span class="ssp-optional">All fields are optional</span>
                <button type="button" class="ssp-close" aria-label="Close" @click="closeModal">
                  &times;
                </button>
              </div>

              <div class="ssp-sentence">
                <template v-for="(piece, index) in activeTab.sentence" :key="index">
                  <SentenceSlot
                    v-if="piece.field"
                    :model-value="values[activeTab.key][piece.field]"
                    @update:model-value="pick(piece.field, $event)"
                    :field="fields[piece.field]"
                    :search="(keyword) => suggest(piece.field, keyword)"
                  />
                  <span
                    v-else
                    class="ssp-text"
                    :class="{ 'ssp-text-tight': piece === ',' }"
                  >{{ piece }}</span>
                </template>
              </div>

              <div class="ssp-foot">
                <button
                  type="button"
                  class="ssp-clear"
                  :disabled="!filledCount"
                  @click="clearAll"
                >
                  <i class="fa fa-refresh"></i> Reset
                </button>
                <button type="button" class="ssp-build" @click="build">
                  <i class="fa fa-search"></i>
                  {{ activeTab.button }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>
<script>
import SentenceSlot from "@/components/SentenceSlot";
import {
  SEARCH_FIELDS,
  SEARCH_TABS,
  emptyValues,
} from "@/components/sentenceSearchConfig";

// the section pages read the same keys the old global search sent (see
// globelSearchResult in each section view); chip names the filter chip a
// picked value lands in, so the page can name it straight away
const FILTER_KEYS = {
  function: { key: "jid", chip: "job_function" },
  company_name: { key: "coid", chip: "company" },
  company_type: { key: "ctype" },
  company_size: { key: "cs" },
  industry: { key: "iid", chip: "industry" },
  country: { key: "cid", chip: "location" },
  deal_type: { key: "tid", chip: "deal_type" },
};

// sentence field a picked company fills in -> the company suggestion column
// holding its value
const COMPANY_DETAIL_FIELDS = {
  company_type: "company_type",
  company_size: "company_size",
  industry: "industry_id",
};

export default {
  name: "SentenceSearch",
  components: { SentenceSlot },
  data() {
    const values = {};
    SEARCH_TABS.forEach((tab) => {
      values[tab.key] = emptyValues(tab);
    });
    return {
      tabs: SEARCH_TABS,
      fields: SEARCH_FIELDS,
      // kept per tab, so reopening a popup shows what was entered before
      values,
      activeTab: null,
    };
  },
  computed: {
    activeFields() {
      return this.activeTab ? Object.keys(this.values[this.activeTab.key]) : [];
    },
    filledCount() {
      return this.activeFields.filter((key) => this.summaryValue(key)).length;
    },
  },
  beforeUnmount() {
    this.closeModal();
  },
  methods: {
    summaryValue(key) {
      const value = this.values[this.activeTab.key][key];
      return Array.isArray(value)
        ? value.map((item) => item.name).join(", ")
        : value && value.name;
    },
    openTab(tab) {
      this.activeTab = tab;
      document.body.classList.add("ssp-lock");
      window.addEventListener("keydown", this.onKeydown);
    },
    closeModal() {
      this.activeTab = null;
      document.body.classList.remove("ssp-lock");
      window.removeEventListener("keydown", this.onKeydown);
    },
    onKeydown(event) {
      if (event.key === "Escape") {
        this.closeModal();
      }
    },
    pick(field, value) {
      const values = this.values[this.activeTab.key];
      values[field] = value;
      // a picked company fills in its own type, size and industry where the
      // sentence has those fields
      if (field === "company_name" && value) {
        Object.keys(COMPANY_DETAIL_FIELDS).forEach((detail) => {
          const selectedId = value[COMPANY_DETAIL_FIELDS[detail]];
          if (!(detail in values) || !selectedId) {
            return;
          }
          this.$api
            .getAPI({
              _action: "/sentence-search/suggest",
              _body: { field: detail, selected_id: selectedId },
            })
            .then((res) => {
              const option = (res.list || [])[0];
              // skip if the company was changed or cleared meanwhile
              if (option && values.company_name && values.company_name.id === value.id) {
                values[detail] = option;
              }
            })
            .catch(() => {});
        });
      }
    },
    suggest(field, keyword) {
      return this.$api
        .getAPI({
          _action: "/sentence-search/suggest",
          _body: { field: field, keyword: keyword },
        })
        .then((res) => res.list || []);
    },
    toFilters(values) {
      const filters = { labels: {}, skills: [] };
      Object.keys(values).forEach((field) => {
        const value = values[field];
        if (Array.isArray(value)) {
          // skills and tags both filter on skill ids
          filters.skills.push(...value);
        } else if (value) {
          const target = FILTER_KEYS[field];
          filters[target.key] = value.id;
          if (target.chip) {
            filters.labels[target.chip] = value.name;
          }
        }
      });
      return filters;
    },
    clearAll() {
      this.values[this.activeTab.key] = emptyValues(this.activeTab);
    },
    build() {
      const tab = this.activeTab;
      this.closeModal();
      if (!localStorage.getItem("token")) {
        this.$router.push("/login");
        return;
      }
      // the section page reads and removes this on mount
      this.$shareService.setMapValue("global_search_filter", this.toFilters(this.values[tab.key]));
      this.$router.push(tab.route);
    },
  },
};
</script>
<style>
/* unscoped: the popup locks the page behind it */
body.ssp-lock {
  overflow: hidden;
}
</style>
<style scoped>
/* ---- dark band - takes the place of the old global search bar ---- */
.ssb {
  position: relative;
  margin-top: 16px;
  padding: 22px 0;
  overflow: hidden;
  background-color: #1b2430;
  background-image: radial-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px);
  background-size: 18px 18px;
}

/* the brand's warm glow, from the right edge */
.ssb::before {
  content: "";
  position: absolute;
  top: -120px;
  right: -120px;
  width: 520px;
  height: 320px;
  background: radial-gradient(
    50% 50% at 50% 50%,
    rgba(222, 74, 56, 0.35) 0%,
    rgba(222, 74, 56, 0) 70%
  );
  pointer-events: none;
}

.ssb-inner {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 18px 32px;
}

.ssb-eyebrow {
  font-family: "Calibri Bold", "Segoe UI", system-ui, sans-serif;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #f08a7c;
}

.ssb-heading {
  margin: 2px 0 0;
  font-family: "Calibri Bold", "Segoe UI", system-ui, sans-serif;
  font-size: 24px;
  color: #fff;
}

.ssb-seg {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 5px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
}

.ssb-seg-btn {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 11px 22px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  font-family: "Calibri Bold", "Segoe UI", system-ui, sans-serif;
  font-size: 16px;
  color: #e4e7ec;
  cursor: pointer;
  transition: background 0.18s, color 0.18s, box-shadow 0.18s;
}

.ssb-seg-btn .fa {
  font-size: 17px;
  color: var(--tab-color);
  filter: brightness(1.35);
  transition: filter 0.18s;
}

.ssb-seg-btn:hover,
.ssb-seg-btn:focus-visible {
  background: #fff;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
  color: #101828;
  outline: 0;
}

.ssb-seg-btn:hover .fa,
.ssb-seg-btn:focus-visible .fa {
  filter: none;
}

/* ---- popup ---- */
.ssp-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 10vh 16px 24px;
  overflow-y: auto;
  background: rgba(16, 24, 40, 0.6);
  backdrop-filter: blur(3px);
}

.ssp {
  display: grid;
  grid-template-columns: 270px 1fr;
  width: 100%;
  max-width: 1000px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.35);
}

.ssp-aside {
  position: relative;
  padding: 26px 22px;
  overflow: hidden;
  background: #1b2430;
  border-radius: 16px 0 0 16px;
  color: #fff;
}

/* the tab colour bleeding in from the corner */
.ssp-aside::before {
  content: "";
  position: absolute;
  top: -90px;
  left: -90px;
  width: 260px;
  height: 260px;
  background: var(--tab-color);
  border-radius: 50%;
  opacity: 0.22;
  filter: blur(40px);
  pointer-events: none;
}

.ssp-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  background: var(--tab-color);
  border-radius: 12px;
  font-size: 21px;
  color: #fff;
}

.ssp-title {
  position: relative;
  margin: 14px 0 4px;
  font-family: "Calibri Bold", "Segoe UI", system-ui, sans-serif;
  font-size: 21px;
  color: #fff;
}

.ssp-hint {
  position: relative;
  margin: 0;
  font-family: "Calibri Regular", "Segoe UI", system-ui, sans-serif;
  font-size: 14px;
  line-height: 1.4;
  color: #a5adba;
}

.ssp-summary {
  position: relative;
  margin-top: 22px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.ssp-summary-head {
  margin-bottom: 8px;
  font-family: "Calibri Bold", "Segoe UI", system-ui, sans-serif;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #f08a7c;
}

.ssp-summary-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.08);
  font-family: "Calibri Regular", "Segoe UI", system-ui, sans-serif;
  font-size: 13px;
}

.ssp-summary-label {
  flex: 0 0 auto;
  color: #a5adba;
}

.ssp-summary-value {
  text-align: right;
  color: #fff;
  word-break: break-word;
}

.ssp-summary-value.ssp-any {
  color: #667085;
  font-style: italic;
}

.ssp-main {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 18px 28px 22px;
}

.ssp-main-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ssp-step {
  font-family: "Calibri Bold", "Segoe UI", system-ui, sans-serif;
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #101828;
}

.ssp-optional {
  flex: 1 1 auto;
  font-family: "Calibri Regular", "Segoe UI", system-ui, sans-serif;
  font-size: 13px;
  color: #98a2b3;
}

.ssp-close {
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  font-size: 26px;
  line-height: 1;
  color: #667085;
  cursor: pointer;
}

.ssp-close:hover {
  background: #f2f4f7;
  color: #101828;
}

.ssp-sentence {
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  align-items: center;
  align-content: flex-start;
  gap: 14px 10px;
  padding: 30px 0 34px;
}

.ssp-text {
  font-family: "Calibri Regular", "Segoe UI", system-ui, sans-serif;
  font-size: 20px;
  color: #475467;
  white-space: nowrap;
}

.ssp-text-tight {
  margin-left: -7px;
}

.ssp-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  border-top: 1px solid #eef0f3;
}

.ssp-clear {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fff;
  font-family: "Calibri Regular", "Segoe UI", system-ui, sans-serif;
  font-size: 14px;
  color: #475467;
  cursor: pointer;
}

.ssp-clear:hover:not(:disabled) {
  background: #f9fafb;
}

.ssp-clear:disabled {
  opacity: 0.45;
  cursor: default;
}

.ssp-build {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 12px 26px;
  border: 0;
  border-radius: 999px;
  background: #de4a38;
  box-shadow: 0 8px 18px rgba(222, 74, 56, 0.32);
  font-family: "Calibri Bold", "Segoe UI", system-ui, sans-serif;
  font-size: 16px;
  color: #fff;
  cursor: pointer;
  transition: background 0.15s, transform 0.15s;
}

.ssp-build:hover {
  background: #c93b2a;
  transform: translateY(-1px);
}

.ssp-fade-enter-active,
.ssp-fade-leave-active {
  transition: opacity 0.2s;
}

.ssp-fade-enter-active .ssp,
.ssp-fade-leave-active .ssp {
  transition: transform 0.2s;
}

.ssp-fade-enter-from,
.ssp-fade-leave-to {
  opacity: 0;
}

.ssp-fade-enter-from .ssp,
.ssp-fade-leave-to .ssp {
  transform: translateY(14px) scale(0.98);
}

@media (max-width: 991px) {
  .ssb-inner {
    justify-content: center;
    text-align: center;
  }

  .ssb-seg {
    justify-content: center;
  }
}

@media (max-width: 767px) {
  .ssb-seg {
    border-radius: 14px;
  }

  .ssb-seg-btn {
    flex: 1 1 calc(50% - 4px);
    justify-content: center;
    border-radius: 10px;
  }

  .ssp-overlay {
    padding-top: 24px;
  }

  .ssp {
    grid-template-columns: 1fr;
  }

  .ssp-aside {
    border-radius: 16px 16px 0 0;
  }

  /* the sentence itself is the read-back on a phone */
  .ssp-summary {
    display: none;
  }

  .ssp-main {
    padding: 16px 18px 18px;
  }

  .ssp-text {
    font-size: 18px;
    white-space: normal;
  }

  .ssp-build {
    flex: 1 1 auto;
    justify-content: center;
    margin-left: 12px;
  }
}
</style>
